import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Calendar, 
  FileText, 
  Clock,
  CheckCircle,
  Circle,
  Info,
  Warning,
  ArrowSquareOut,
  ShieldCheck,
  CaretDown,
  CaretUp,
  ChatCircleDots,
  ArrowRight
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import StatusTimeline from '../components/StatusTimeline';
import { SearchReportFull } from '../components/SearchReportCard';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

import API_URL from '../config';

function formatDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

const URGENCY_STYLES = {
  overdue: { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', label: 'Overdue' },
  urgent:  { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', label: 'Urgent' },
  soon:    { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', label: 'Upcoming' },
  normal:  { bg: '#f0fdf4', border: '#86efac', text: '#166534', label: 'On track' },
};

function renderTmField(value) {
  if (value == null) return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    return value.map(v => {
      if (v == null) return '';
      if (typeof v === 'string' || typeof v === 'number') return String(v);
      return v.value || v.name || v.label || v.description || JSON.stringify(v);
    }).filter(Boolean).join('; ');
  }
  if (typeof value === 'object') return value.value || value.name || value.label || JSON.stringify(value);
  return String(value);
}

function formatCountdown(days) {
  if (days < 0) return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day remaining';
  return `${days} days remaining`;
}

const STATUS_CONFIG = {
  onboarding: { label: 'In Progress', class: 'status-onboarding', description: "Your matter is underway. You'll see USPTO details here once your application is filed." },
  filed: { label: 'Filed', class: 'status-filed', description: 'Your application has been submitted to the USPTO.' },
  office_action: { label: 'Office Action Received', class: 'status-drafting', description: 'The USPTO has issued an Office Action. Our team is preparing a response.' },
  office_action_cleared: { label: 'Office Action Cleared', class: 'status-search', description: 'Great news — the Office Action has been resolved and your application is moving forward.' },
  published: { label: 'Published for Opposition', class: 'status-pending', description: 'Your mark has been published in the Official Gazette. Third parties have 30 days to oppose.' },
  opposition_cleared: { label: 'Opposition Cleared', class: 'status-onboarding', description: 'No opposition was filed. Your trademark is on its way to registration.' },
  registered: { label: 'Registered', class: 'status-registered', description: 'Congratulations! Your trademark is officially registered with the USPTO.' },
  dead: { label: 'Abandoned / Canceled', class: 'status-dead', description: 'This trademark application is no longer active with the USPTO.' }
};

function countdownText(days) {
  if (days == null) return '';
  if (days < 0) return 'OVERDUE';
  if (days === 0) return 'today';
  if (days < 30) return `in ${days} day${days === 1 ? '' : 's'}`;
  if (days < 365) {
    const m = Math.round(days / 30);
    return `in ${m} month${m === 1 ? '' : 's'}`;
  }
  const y = (days / 365).toFixed(1).replace(/\.0$/, '');
  return `in ${y} year${y === '1' ? '' : 's'}`;
}

function DeadlineCard({ d, idx }) {
  const style = URGENCY_STYLES[d.urgency] || URGENCY_STYLES.normal;
  const isUrgent = d.urgency === 'overdue' || d.urgency === 'urgent';
  const isMaintenance = !!d.stage && !!d.window_opens;
  // Collapse maintenance cards by default unless they're urgent (urgent/overdue
  // = we want the milestone detail front-and-center).
  const [expanded, setExpanded] = useState(isMaintenance ? isUrgent : true);

  const milestoneState = (key) => {
    if (!isMaintenance) return 'future';
    if (d.stage === 'upcoming') return key === 'opens' ? 'active' : 'future';
    if (d.stage === 'open')     return key === 'opens' ? 'before' : key === 'closes' ? 'active' : 'future';
    if (d.stage === 'grace')    return key === 'grace' ? 'active' : 'before';
    return key === 'grace' ? 'active' : 'before'; // overdue
  };
  const milestoneIcon = (state) => {
    if (state === 'before') return <CheckCircle weight="fill" className="w-4 h-4 text-emerald-500 flex-shrink-0" />;
    if (state === 'active') return (
      <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
        <div className="w-3 h-3 rounded-full bg-[#141b4d] ring-4 ring-[#141b4d]/15" />
      </div>
    );
    return <Circle weight="regular" className="w-4 h-4 text-[#a1a1aa] flex-shrink-0" />;
  };
  const milestoneRow = (key, label, date, hint) => {
    const state = milestoneState(key);
    const dateTone =
      state === 'before' ? 'text-[#71717A]' :
      state === 'active' ? 'text-[#141b4d] font-semibold' :
      'text-[#52525B]';
    const labelTone =
      state === 'before' ? 'text-[#71717A] line-through decoration-[#a1a1aa]/40' :
      state === 'active' ? 'text-[#09090B] font-medium' :
      'text-[#3f3f46]';
    return (
      <div className="flex items-center gap-2.5 text-sm">
        {milestoneIcon(state)}
        <span className={`flex-1 ${labelTone}`}>
          {label}{hint && <span className="text-[#a1a1aa] font-normal"> {hint}</span>}
        </span>
        <span className={`tabular-nums ${dateTone}`}>{formatDate(date)}</span>
      </div>
    );
  };

  return (
    <div
      className="p-4"
      style={{ backgroundColor: style.bg, border: `1px solid ${style.border}` }}
      data-testid={`deadline-${idx}`}
    >
      <div className="flex items-start gap-3">
        {isUrgent ? (
          <Warning weight="fill" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: style.text }} />
        ) : (
          <Clock weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: style.text }} />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <p className="font-semibold" style={{ color: style.text }}>
              {d.label}
            </p>
            <span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: style.text, color: style.bg }}>
              {isMaintenance
                ? `${d.actionable_label} ${countdownText(d.days_to_actionable)}`
                : style.label}
            </span>
          </div>
          {/* Collapsed maintenance summary: one-line key date so clients see what's next at a glance */}
          {isMaintenance && !expanded && (
            <p className="text-sm mt-1" style={{ color: style.text }}>
              {d.actionable_label}{' '}
              <span className="font-medium">{formatDate(d.actionable_date)}</span>
              <span className="mx-2">·</span>
              {countdownText(d.days_to_actionable)}
            </p>
          )}
          {!isMaintenance && (
            <p className="text-sm mt-1" style={{ color: style.text }}>
              <span className="font-medium">{formatDate(d.due_date)}</span>
              <span className="mx-2">·</span>
              {formatCountdown(d.days_remaining)}
            </p>
          )}
          {d.description && (
            <p className="text-xs mt-1" style={{ color: '#52525B' }}>{d.description}</p>
          )}
        </div>
      </div>

      {/* 3-step milestone checklist — only when expanded */}
      {isMaintenance && expanded && (
        <div className="space-y-1.5 mt-3 pt-3 border-t" style={{ borderColor: style.border }}>
          {milestoneRow('opens', 'Filing window opens', d.window_opens, '(earliest date to file)')}
          {milestoneRow('closes', 'Standard deadline', d.deadline_closes, '(last day at standard fee)')}
          {milestoneRow('grace', 'Grace period ends', d.grace_ends, '(+extra fee)')}
        </div>
      )}

      {/* Expand / collapse toggle (maintenance only) */}
      {isMaintenance && (
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="mt-2 ml-8 inline-flex items-center gap-1 text-xs font-medium hover:underline"
          style={{ color: style.text }}
          data-testid={`deadline-toggle-${idx}`}
        >
          {expanded ? (
            <>Hide milestone dates <CaretUp weight="bold" className="w-3 h-3" /></>
          ) : (
            <>Show window, grace period <CaretDown weight="bold" className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
}

export default function MatterDetail() {
  const { matterId } = useParams();
  const { user } = useAuth();
  const [matter, setMatter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMatter();
  }, [matterId]);

  const fetchMatter = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/client/matters/${matterId}`, {
        
      });
      setMatter(response.data);
    } catch (error) {
      toast.error('Failed to load matter details');
      console.error('Error fetching matter:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#FFD60A] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!matter) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-[#52525B]">Matter not found.</p>
          <Link to="/dashboard" className="btn-primary mt-4 inline-block">
            Back to Dashboard
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const statusInfo = STATUS_CONFIG[matter.status_phase] || STATUS_CONFIG.onboarding;

  return (
    <DashboardLayout>
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
        data-testid="matter-detail"
      >
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#09090B] transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft weight="bold" className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <p className="label-uppercase text-[#52525B] mb-1">Trademark Matter</p>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#141b4d' }}>
              {matter.trademark_name}
              {matter.mark_drawing_type && matter.mark_drawing_type.toUpperCase() !== 'STANDARD_CHARACTERS' && matter.mark_drawing_type.toUpperCase() !== 'TYPED_DRAWING' && matter.mark_drawing_type !== '' && (
                <span className="ml-3 text-sm font-medium px-2 py-1 align-middle" style={{ backgroundColor: '#141b4d', color: '#ffd100' }}>LOGO</span>
              )}
            </h1>
            {matter.description && (
              <p className="text-[#52525B] mt-2">{matter.description}</p>
            )}
          </div>
          <span className={`status-badge ${statusInfo.class} text-base px-4 py-2`}>
            {statusInfo.label}
          </span>
        </div>

        {/* Status Timeline */}
        <div className="card-swiss">
          <h2 className="text-lg font-semibold text-[#09090B] mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Application Progress
          </h2>
          <StatusTimeline currentPhase={matter.status_phase} journeyPhase={matter.journey_phase} showLabels={true} />
          
          <div className="mt-6 p-4 bg-[#F4F4F5] flex items-start gap-3">
            <Info weight="duotone" className="w-5 h-5 text-[#3B82F6] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-[#09090B]">Current Phase: {statusInfo.label}</p>
              <p className="text-sm text-[#52525B] mt-1">{statusInfo.description}</p>
            </div>
          </div>
        </div>

        {/* Search Report (Phase 4) — shows when a report is published, or an empty state at Search Completed */}
        <SearchReportFull
          matter={matter}
          onView={() => {
            axios.post(`${API_URL}/api/client/matters/${matter.id}/report-viewed`).catch(() => {});
          }}
        />

        {/* Book-a-call nudge — emphasized during an Office Action, softer otherwise */}
        {matter.status_phase === 'office_action' ? (
          <Link
            to="/book-call"
            className="block group transition-all hover:shadow-md"
            style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderLeft: '4px solid #f59e0b' }}
            data-testid="matter-book-call-nudge-office-action"
          >
            <div className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.15)' }}>
                <ChatCircleDots weight="duotone" className="w-6 h-6" style={{ color: '#92400e' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="label-uppercase text-xs font-semibold mb-1" style={{ color: '#92400e' }}>
                  Have questions about this Office Action?
                </p>
                <p className="font-semibold text-base leading-snug" style={{ color: '#09090B' }}>
                  Talk it through on a free check-in call.
                </p>
                <p className="text-sm mt-1.5 leading-relaxed text-[#3f3f46]">
                  Office Actions can feel intimidating, but they're a normal part of the process. Book a free
                  call with our client care team to walk through what it means and what happens next — no charge,
                  no pressure.
                </p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: '#92400e' }}>
                  Book a free check-in call
                  <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </Link>
        ) : matter.status_phase !== 'registered' && matter.status_phase !== 'dead' ? (
          <Link
            to="/book-call"
            className="block group transition-all hover:shadow-md"
            style={{ backgroundColor: '#f2e9db', border: '1px solid #141b4d33' }}
            data-testid="matter-book-call-nudge"
          >
            <div className="p-5 flex items-start gap-4">
              <div className="w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(20,27,77,0.1)' }}>
                <ChatCircleDots weight="duotone" className="w-6 h-6" style={{ color: '#141b4d' }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-base leading-snug" style={{ color: '#141b4d' }}>
                  Questions about where things stand?
                </p>
                <p className="text-sm mt-1.5 leading-relaxed text-[#3f3f46]">
                  You can book a free check-in call with our client care team anytime to talk through your
                  application, a status update, or anything you've seen on this page.
                </p>
                <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: '#1d57a5' }}>
                  Book a free check-in call
                  <ArrowRight weight="bold" className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </Link>
        ) : null}

        {/* USPTO Records & Deadlines */}
        {(matter.serial_number || matter.registration_number) && (
          <div className="card-swiss" data-testid="uspto-records-card">
            <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
              <div>
                <p className="label-uppercase mb-1" style={{ color: '#1d57a5' }}>USPTO Records</p>
                <h2 className="text-lg font-semibold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#141b4d' }}>
                  Official Filing & Deadlines
                </h2>
              </div>
              {matter.tsdr_links?.status_url && (
                <div className="flex flex-col items-end gap-1">
                  <a
                    href={matter.tsdr_links.status_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-[#141b4d] text-[#141b4d] hover:bg-[#141b4d] hover:text-[#ffd100] transition-colors"
                    data-testid="tsdr-status-link"
                  >
                    View on USPTO TSDR
                    <ArrowSquareOut weight="bold" className="w-4 h-4" />
                  </a>
                  <p className="text-xs mt-1 max-w-xs text-right" style={{ color: '#9a9488' }}>
                    On TSDR, click the <span className="font-medium" style={{ color: '#141b4d' }}>Documents</span> tab to review every filing submitted with this trademark.
                  </p>
                </div>
              )}
            </div>

            {(matter.tsdr_summary?.current_status || matter.tm_tko_data?.statusCode) && (
              <div className="mb-4 pb-4 border-b border-[#e7e2d6]">
                <p className="text-xs uppercase tracking-wider mb-1" style={{ color: '#9a9488' }}>Current USPTO Status</p>
                <p className="font-medium" style={{ color: '#141b4d' }}>
                  {matter.tsdr_summary?.current_status || `Status Code ${matter.tm_tko_data?.statusCode}`}
                  {(matter.tsdr_summary?.status_date || matter.tm_tko_data?.statusDate) && (
                    <span className="text-sm font-normal ml-2" style={{ color: '#52525B' }}>
                      (as of {formatDate(matter.tsdr_summary?.status_date || String(matter.tm_tko_data?.statusDate || '').replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'))})
                    </span>
                  )}
                </p>
                {matter.tsdr_summary?.friendly_status ? (
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: '#101820' }}>
                    {matter.tsdr_summary.friendly_status}
                  </p>
                ) : matter.tsdr_summary?.status_explanation ? (
                  <p className="text-sm mt-2 leading-relaxed" style={{ color: '#52525B' }}>
                    {matter.tsdr_summary.status_explanation}
                  </p>
                ) : null}
              </div>
            )}

            {matter.completed_maintenance && matter.completed_maintenance.length > 0 && (
              <div className="space-y-2 mb-5" data-testid="completed-maintenance-section">
                <p className="text-xs uppercase tracking-wider" style={{ color: '#9a9488' }}>Already Filed & Accepted</p>
                <div className="flex flex-wrap gap-2">
                  {matter.completed_maintenance.map((c, idx) => (
                    <div
                      key={`${c.year}-${idx}`}
                      className="inline-flex items-center gap-2 px-3 py-1.5"
                      style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}
                      data-testid={`completed-${c.year}`}
                      title={c.description}
                    >
                      <CheckCircle weight="fill" className="w-4 h-4 flex-shrink-0" style={{ color: '#15803d' }} />
                      <span className="text-sm font-medium" style={{ color: '#14532d' }}>
                        {c.label}
                      </span>
                      {c.date && (
                        <span className="text-xs" style={{ color: '#16803d' }}>
                          · accepted {formatDate(c.date)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {matter.uspto_deadlines && matter.uspto_deadlines.length > 0 ? (
              <div className="space-y-3">
                <p className="text-xs uppercase tracking-wider" style={{ color: '#9a9488' }}>Upcoming Deadlines</p>
                {matter.uspto_deadlines.map((d, idx) => (
                  <DeadlineCard key={idx} d={d} idx={idx} />
                ))}
                <p className="text-xs mt-2" style={{ color: '#9a9488' }}>
                  Deadlines are computed from USPTO prosecution history (TSDR) and standard USPTO rules.
                </p>
              </div>
            ) : (
              <p className="text-sm" style={{ color: '#52525B' }}>
                No upcoming deadlines at this phase.
              </p>
            )}

            {/* Protection Plan upsell — only for non-PP clients on a registered mark */}
            {matter.status_phase === 'registered' && user && !user.has_protection_plan && (
              <Link
                to="/protection-plan"
                className="mt-5 block group transition-all hover:shadow-md"
                style={{
                  background: 'linear-gradient(135deg, #141b4d 0%, #1d2461 100%)',
                  border: '1px solid #ffd100',
                }}
                data-testid="matter-pp-upsell"
              >
                <div className="p-5 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#ffd100' }}>
                    <ShieldCheck weight="fill" className="w-6 h-6" style={{ color: '#141b4d' }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="label-uppercase text-xs font-semibold mb-1" style={{ color: '#ffd100' }}>
                      Protection Plan Benefit
                    </p>
                    <p className="font-semibold text-white text-base leading-snug">
                      Track this mark's renewal deadlines — and never miss one.
                    </p>
                    <p className="text-sm mt-1.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
                      Your trademark is registered, but maintenance filings are due every 5–10 years. Upgrade to the
                      Protection Plan and we'll monitor §8 declarations, 10-year renewals, and watch the USPTO for
                      copycats — all for $1 a day.
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: '#ffd100' }}>
                      Learn more about Protection Plan
                      <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            )}

            {/* Protection Plan callout — for PP clients on a registered mark with maintenance deadlines */}
            {matter.status_phase === 'registered' && user?.has_protection_plan && (() => {
              const maintenance = (matter.uspto_deadlines || [])
                .filter(d => d.stage && d.window_opens)
                .sort((a, b) => (a.actionable_date || '').localeCompare(b.actionable_date || ''));
              const next = maintenance[0];
              return (
                <Link
                  to="/protection-plan"
                  className="mt-5 block group transition-all hover:shadow-md"
                  style={{
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #86efac',
                    borderLeft: '4px solid #22C55E',
                  }}
                  data-testid="matter-pp-callout"
                >
                  <div className="p-5 flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(34,197,94,0.15)' }}>
                      <ShieldCheck weight="duotone" className="w-6 h-6" style={{ color: '#16803d' }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="label-uppercase text-xs font-semibold mb-1" style={{ color: '#16803d' }}>
                        Protection Plan · Active
                      </p>
                      {next ? (
                        <>
                          <p className="font-semibold text-[#09090B] text-base leading-snug">
                            Heads up: your next renewal milestone for this mark is coming.
                          </p>
                          <p className="text-sm mt-1.5 leading-relaxed text-[#3f3f46]">
                            <span className="font-medium text-[#09090B]">{next.label}</span> — {next.actionable_label.toLowerCase()}{' '}
                            <span className="font-medium text-[#09090B]">{formatDate(next.actionable_date)}</span>.
                            We're tracking it and we'll reach out well before the window closes. No action needed yet.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-semibold text-[#09090B] text-base leading-snug">
                            This mark is registered and protected.
                          </p>
                          <p className="text-sm mt-1.5 leading-relaxed text-[#3f3f46]">
                            No maintenance deadlines on the immediate horizon. We're scanning the USPTO twice daily
                            and will give you plenty of heads-up before any §8 or §9 filing window opens.
                          </p>
                        </>
                      )}
                      <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: '#16803d' }}>
                        View all renewal deadlines on Protection Plan
                        <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })()}

            {/* Prosecution History (TSDR events) */}
            {matter.tsdr_summary?.events && matter.tsdr_summary.events.length > 0 && (
              <div className="mt-6 pt-6 border-t border-[#e7e2d6]">
                <p className="text-xs uppercase tracking-wider mb-3" style={{ color: '#9a9488' }}>Recent USPTO Events</p>
                <div className="space-y-2" data-testid="prosecution-history">
                  {matter.tsdr_summary.events.slice(0, 6).map((evt, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <span className="font-mono text-xs px-2 py-0.5 flex-shrink-0 mt-0.5" style={{ backgroundColor: '#f2e9db', color: '#141b4d' }}>
                        {formatDate(evt.date) || '—'}
                      </span>
                      <span style={{ color: '#101820' }}>{evt.description}</span>
                    </div>
                  ))}
                  {matter.tsdr_summary.events.length > 6 && (
                    <p className="text-xs pt-1" style={{ color: '#9a9488' }}>
                      Showing 6 of {matter.tsdr_summary.events.length} events — see TSDR Documents for full history.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Admin-only TSDR debug panel — visible during admin preview-as-client */}
            {matter.tsdr_debug && (
              <details
                className="mt-6 pt-4 border-t border-dashed"
                style={{ borderColor: '#9a9488' }}
                data-testid="tsdr-debug-panel"
              >
                <summary className="cursor-pointer text-xs font-mono uppercase tracking-wider select-none" style={{ color: '#9a9488' }}>
                  TSDR debug · admin only
                  {matter.tsdr_debug.unrecognized_maintenance_events?.length > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: '#fef3c7', color: '#92400e' }}>
                      {matter.tsdr_debug.unrecognized_maintenance_events.length} unrecognized
                    </span>
                  )}
                </summary>
                <div className="mt-3 space-y-3 text-xs font-mono" style={{ color: '#3f3f46' }}>
                  <div>
                    <span className="text-[#9a9488]">accepted_maintenance_years:</span>{' '}
                    <span style={{ color: '#16803d' }}>
                      [{(matter.tsdr_debug.accepted_maintenance_years || []).join(', ') || '—'}]
                    </span>
                  </div>
                  <div>
                    <span className="text-[#9a9488]">sec15_acknowledged:</span>{' '}
                    <span style={{ color: matter.tsdr_debug.sec15_acknowledged ? '#16803d' : '#9a9488' }}>
                      {String(matter.tsdr_debug.sec15_acknowledged)}
                    </span>
                  </div>
                  <div>
                    <span className="text-[#9a9488]">total_events:</span> {matter.tsdr_debug.total_events}
                  </div>

                  {matter.tsdr_debug.matched_events?.length > 0 && (
                    <div>
                      <p className="text-[#9a9488] mb-1">Matched maintenance events ({matter.tsdr_debug.matched_events.length}):</p>
                      <ul className="space-y-1.5 pl-3">
                        {matter.tsdr_debug.matched_events.map((e, i) => (
                          <li key={i} className="border-l-2 pl-2" style={{ borderColor: '#22C55E' }}>
                            <span style={{ color: '#16803d' }}>{e.date}</span>{' '}
                            <span className="font-bold">{e.code}</span> — {e.description}
                            <br />
                            <span className="text-[#9a9488]">matched: [{e.matched.join(', ')}]</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {matter.tsdr_debug.unrecognized_maintenance_events?.length > 0 && (
                    <div>
                      <p className="mb-1" style={{ color: '#92400e' }}>
                        ⚠ Unrecognized maintenance-looking events ({matter.tsdr_debug.unrecognized_maintenance_events.length}):
                      </p>
                      <ul className="space-y-1.5 pl-3">
                        {matter.tsdr_debug.unrecognized_maintenance_events.map((e, i) => (
                          <li key={i} className="border-l-2 pl-2" style={{ borderColor: '#f59e0b' }}>
                            <span style={{ color: '#92400e' }}>{e.date}</span>{' '}
                            <span className="font-bold">{e.code}</span> — {e.description}
                          </li>
                        ))}
                      </ul>
                      <p className="text-[#9a9488] mt-2">
                        ↳ If these should affect deadline logic, add their codes or update the regex in <code>server.py</code>.
                      </p>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Information */}
          <div className="card-swiss">
            <h2 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Key Information
            </h2>
            <div className="space-y-4">
              {matter.serial_number && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center flex-shrink-0">
                    <FileText weight="duotone" className="w-5 h-5 text-[#09090B]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#52525B] uppercase tracking-wider">Serial Number</p>
                    <p className="font-medium text-[#09090B]">{matter.serial_number}</p>
                  </div>
                </div>
              )}
              
              {matter.registration_number && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle weight="duotone" className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#52525B] uppercase tracking-wider">Registration Number</p>
                    <p className="font-medium text-[#09090B]">{matter.registration_number}</p>
                  </div>
                </div>
              )}
              
              {!matter.serial_number && !matter.registration_number && (
                <p className="text-[#52525B] text-sm">
                  Filing details will appear here once your application is submitted.
                </p>
              )}
            </div>
          </div>

          {/* Important Dates */}
          <div className="card-swiss">
            <h2 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Important Dates
            </h2>
            <div className="space-y-4">
              {matter.filing_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F4F4F5] flex items-center justify-center flex-shrink-0">
                    <Calendar weight="duotone" className="w-5 h-5 text-[#09090B]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#52525B] uppercase tracking-wider">Filing Date</p>
                    <p className="font-medium text-[#09090B]">{formatDate(matter.filing_date)}</p>
                  </div>
                </div>
              )}
              
              {matter.registration_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#22C55E]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle weight="duotone" className="w-5 h-5 text-[#22C55E]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#52525B] uppercase tracking-wider">Registration Date</p>
                    <p className="font-medium text-[#09090B]">{formatDate(matter.registration_date)}</p>
                  </div>
                </div>
              )}
              
              {matter.renewal_date && (
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#F59E0B]/10 flex items-center justify-center flex-shrink-0">
                    <Clock weight="duotone" className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <p className="text-xs text-[#52525B] uppercase tracking-wider">Renewal Deadline</p>
                    <p className="font-medium text-[#09090B]">{formatDate(matter.renewal_date)}</p>
                  </div>
                </div>
              )}
              
              {!matter.filing_date && !matter.registration_date && !matter.renewal_date && (
                <p className="text-[#52525B] text-sm">
                  Key dates will appear here as your application progresses.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Notes */}
        {matter.notes && (
          <div className="card-swiss">
            <h2 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Notes from Your Legal Team
            </h2>
            <p className="text-[#52525B] whitespace-pre-wrap">{matter.notes}</p>
          </div>
        )}

        {/* Trademark Details */}
        {matter.tm_tko_data && (
          <div className="card-swiss">
            <h2 className="text-lg font-semibold text-[#09090B] mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              Trademark Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {matter.tm_tko_data.markIdentification && (
                <div>
                  <p className="text-xs text-[#52525B] uppercase tracking-wider mb-1">Mark</p>
                  <p className="font-medium text-[#09090B]">{matter.tm_tko_data.markIdentification}</p>
                </div>
              )}
              {matter.international_classes && matter.international_classes.length > 0 && (
                <div>
                  <p className="text-xs text-[#52525B] uppercase tracking-wider mb-1">
                    {matter.international_classes.length === 1 ? 'International Class' : 'International Classes'}
                  </p>
                  <p className="font-medium text-[#09090B]">
                    {matter.international_classes.map(c => `Class ${parseInt(c, 10)}`).join(', ')}
                  </p>
                </div>
              )}
              {matter.tm_tko_data.owners && (
                <div>
                  <p className="text-xs text-[#52525B] uppercase tracking-wider mb-1">Owner</p>
                  <p className="font-medium text-[#09090B]">{renderTmField(matter.tm_tko_data.owners)}</p>
                </div>
              )}
              {matter.tm_tko_data.goodsAndServices && (
                <div className="md:col-span-2">
                  <p className="text-xs text-[#52525B] uppercase tracking-wider mb-1">Goods & Services</p>
                  <p className="text-[#09090B]">{renderTmField(matter.tm_tko_data.goodsAndServices)}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact callout — gently steers clients to reply in the existing thread */}
        <div className="card-swiss" style={{ backgroundColor: '#f2e9db', borderTop: '3px solid #ffd100', border: 'none' }}>
          <div className="flex items-start gap-3">
            <Info weight="duotone" className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1d57a5' }} />
            <p className="text-sm leading-relaxed" style={{ color: '#101820' }}>
              For the fastest reply, just respond to your most recent email from us. If you'd prefer to start a fresh message, you can email us at{' '}
              <a
                href="mailto:clients@indielaw.com"
                className="font-medium hover:underline"
                style={{ color: '#1d57a5' }}
                data-testid="contact-cta-mailto"
              >
                clients@indielaw.com
              </a>{' '}
              at any time.
            </p>
          </div>
        </div>
      </motion.div>
    </DashboardLayout>
  );
}
